/**
 * Work-authorization resolution (QA 2026-09: work auth displays under
 * Employment, not Rental Status).
 *
 * The screening form moved the Work Authorization questions from RentalStep to
 * EmploymentStep, so NEW applications store `immigrationStatus` /
 * `immigrationStatusExpiration` in the EMPLOYMENT step. Older applications
 * (and legacy guarantor data) carry them in the RENTAL step — in the plain
 * keys, or the *FirstRenter keys for living-situation statuses
 * (first-time-renter / other / own-home). Every display surface (detail page
 * and PDFs) resolves through this one helper so old and new reports both
 * surface the answer in the Employment section, and the two surfaces can't
 * drift.
 *
 * Structural input types on purpose: the page passes ParsedStepData steps and
 * the PDFs pass report-template-data blocks — both carry these exact keys.
 */

type EmploymentWorkAuthSource = {
	immigrationStatus?: string | null
	immigrationStatusExpiration?: string | null
} | null

type RentalWorkAuthSource = {
	rentalStatus?: string | null
	immigrationStatus?: string | null
	immigrationStatusExpiration?: string | null
	immigrationStatusFirstRenter?: string | null
	immigrationStatusExpirationFirstRenter?: string | null
} | null

export type ResolvedWorkAuthorization = {
	status?: string
	expiration?: string
}

const LIVING_SITUATION_STATUSES = ['first-time-renter', 'other', 'own-home']

function present(value: string | null | undefined): value is string {
	return typeof value === 'string' && value.trim() !== ''
}

export function resolveWorkAuthorization({
	employment,
	rental,
}: {
	employment?: EmploymentWorkAuthSource
	rental?: RentalWorkAuthSource
}): ResolvedWorkAuthorization {
	// Current flow: EmploymentStep collects work auth. When the employment step
	// answered (either field), it is authoritative — never mix its status with a
	// rental-step expiration or vice versa.
	if (
		present(employment?.immigrationStatus) ||
		present(employment?.immigrationStatusExpiration)
	) {
		return {
			status: employment?.immigrationStatus?.trim() || undefined,
			expiration: employment?.immigrationStatusExpiration?.trim() || undefined,
		}
	}

	if (!rental) return {}
	const status = rental.rentalStatus?.trim() ?? ''
	// Living-situation statuses stored work auth in the *FirstRenter keys, but
	// LEGACY guarantor data (before RentalStep moved those statuses onto the
	// *FirstRenter keys) kept the PLAIN keys — same fallback the rental card
	// used before the fields moved here.
	if (LIVING_SITUATION_STATUSES.includes(status)) {
		const frStatus =
			rental.immigrationStatusFirstRenter || rental.immigrationStatus
		const frExpiration =
			rental.immigrationStatusExpirationFirstRenter ||
			rental.immigrationStatusExpiration
		if (present(frStatus) || present(frExpiration)) {
			return {
				status: frStatus?.trim() || undefined,
				expiration: frExpiration?.trim() || undefined,
			}
		}
		return {}
	}
	if (
		present(rental.immigrationStatus) ||
		present(rental.immigrationStatusExpiration)
	) {
		return {
			status: rental.immigrationStatus?.trim() || undefined,
			expiration: rental.immigrationStatusExpiration?.trim() || undefined,
		}
	}
	return {}
}
